import React, { Fragment, useState, useEffect } from "react";
import { ReactSVG } from "react-svg";
import { useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";

import { Menu, Transition } from "@headlessui/react";
import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/24/outline";

import { capitalizeFirstLetter, formatDateToDD_MMM_YYYY, formatWordsIntoLines, scrollY } from "../../../../../../helpers/functions";
import ChartNav from "../../../../../common/ChartNav";
import SubNavigation from "../../../common/sub-navigation";
import Medications from "../../../medications/medications";
import ConfirmationModalNew from "../../../../../common/ConfirmationModalNew";
import HistoryTable from './historyTable';
import MedicalHistoryCreate from "../MedicalHistoryCreate";
import Button from "../../../../../common/Button";
import Steps from "../../../Steps";

import { StatusOptions, TypeOptions, DeleteOptions } from './options';
import { getHistoryTypeName, MedicalHistoryTypes } from "../data";
import {
    updateMedicalHistory,
    fetchMedicalHistory,
    createMedicalHistory,
    updateMedicationReview,
    deleteMedicalSurgery,
    fetchMedicalConditions,
} from "../../../../../../redux/modules/clinical-services/actions";
import UncodedMedical from "../../../../../common/tooltip/patient/uncoded-medical";
import NewToolTip from "../../../../../common/New-ToolTip";
import { getValidStep } from "../../../../text/data";
import { useClickOutsideTooltip } from "../../../../../../hooks/useClickOutsideTooltip";


const MedicalHistory = () => {

    const dispatch = useDispatch();
    //#region Data
    const { clinicalServiceObj, medicalHistory, clinicalServiceSteps } =
        useSelector((state) => state.clinicalService);
    const { currentStep, currentSubStep } =
        clinicalServiceObj.initialAssessment.initialAssessmentForm;
    const { id: patientId, serviceId } = useParams();

    const [medicalCondRecords, setMedicalCondRecords] = useState([]);
    const [medicalStudyRecords, setMedicalStudyRecords] = useState([]);
    const [medicalSurgeriesRecords, setMedicalSurgeriesRecords] = useState([]);
    //#endregion

    //#region Pop-ups
    const [showCreateSlider, setShowCreateSlider] = useState(false);
    const [selectedHistory, setSelectedHistory] = useState(null);
    const [selectedHistoryType, setSelectedHistoryType] = useState(null);
    const [create, setCreate] = useState(true);
    const [idToDelete, setIdToDelete] = useState(null);
    const [idToRestore, setIdToRestore] = useState(null);
    const [openDeleteHistory, setOpenDeleteHistory] = useState(false);
    const [openRestoreHistory, setOpenRestoreHistory] = useState(false);
    const [openTooltipId, setOpenTooltipId] = useState(null);
    const [openSurgeryMenuIndex, setOpenSurgeryMenuIndex] = useState(null);
    const [isNavigating, setIsNavigating] = useState(false);
    const tooltipWrapperRef = useClickOutsideTooltip(openTooltipId, setOpenTooltipId);
    //#endregion

    //state management
    useEffect(() => {
        dispatch(fetchMedicalHistory(patientId));
    }, [dispatch, patientId]);

    const MedicalHistoryTab = {
        NoMedicalHisotry: 0,
        MedicalHistoryInformation: 1
    };
    const [selectedTab, setSelectedTab] = useState(null);

    const [isAllHistoryConfirmed, setIsAllHistoryConfirmed] = useState(false);
    const [confirmedMedicalConditions, setConfirmedMedicalConditions] = useState([]);
    const [confirmedMedicalStudies, setConfirmedMedicalStudies] = useState([]);
    
    const serviceConfirmKey = `wasClinicalServiceConfirmed_${serviceId}`;
    // Track if clinical services were previously confirmed (for auto-restore of new medical conditions)
    const [wasClinicalServiceConfirmed, setWasClinicalServiceConfirmed] = useState(() => {
        return sessionStorage.getItem(serviceConfirmKey) === 'true';
    });
    
    // No localStorage for confirmed state; rely on clinical service data.

    useEffect(() => {
        const serviceConfirmed = clinicalServiceObj.initialAssessment?.initialAssessmentForm?.clinicalAssessment?.medicalHistoryConfirmed;
        if (serviceConfirmed === true) {
            setWasClinicalServiceConfirmed(true);
            sessionStorage.setItem(serviceConfirmKey, 'true');
            return;
        }
        if (serviceConfirmed === false) {
            setWasClinicalServiceConfirmed(false);
            setConfirmedMedicalConditions([]);
            setConfirmedMedicalStudies([]);
            sessionStorage.removeItem(serviceConfirmKey);
            sessionStorage.removeItem(`medicalHistoryActiveConditions_${serviceId}`);
            sessionStorage.removeItem(`medicalHistoryActiveStudies_${serviceId}`);
        }
    }, [clinicalServiceObj.initialAssessment?.initialAssessmentForm?.clinicalAssessment?.medicalHistoryConfirmed, serviceConfirmKey, serviceId]);

    const [statusFilter, setstatusFilter] = useState("active");
    const [shownHistoryType, setShownHistoryType] = useState("all");

    // Restore confirmed state when navigating back to the page
    useEffect(() => {
        const storedMedicalHistoryConfirmed = clinicalServiceObj?.initialAssessment?.initialAssessmentForm?.clinicalAssessment?.medicalHistoryConfirmed;
        
        if (storedMedicalHistoryConfirmed === true) {
            setIsAllHistoryConfirmed(true);
        }
    }, [currentStep, clinicalServiceObj?.initialAssessment?.initialAssessmentForm?.clinicalAssessment?.medicalHistoryConfirmed, serviceId]);
    
    const areIdArraysEqual = (left = [], right = []) => left.length === right.length && left.every((value) => right.includes(value));

    // Auto-save confirmed records when they change
    useEffect(() => {
        if (isNavigating || !serviceId) return;

        const existingClinicalAssessment = clinicalServiceObj.initialAssessment?.initialAssessmentForm?.clinicalAssessment;
        const existingConditions = existingClinicalAssessment?.confirmedMedicalConditions ?? [];
        const existingStudies = existingClinicalAssessment?.confirmedMedicalStudies ?? [];

        if (
            areIdArraysEqual(existingConditions, confirmedMedicalConditions) &&
            areIdArraysEqual(existingStudies, confirmedMedicalStudies)
        ) {
            return;
        }

        dispatch(updateMedicationReview(serviceId, {
            clinicalAssessment: {
                ...existingClinicalAssessment,
                confirmedMedicalConditions: confirmedMedicalConditions,
                confirmedMedicalStudies: confirmedMedicalStudies,
            },
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [confirmedMedicalConditions, confirmedMedicalStudies, dispatch, serviceId, isNavigating]);

    const getActiveSnapshotFromStorage = (key) => {
        try {
            const stored = sessionStorage.getItem(key);
            if (stored === null) {
                return null;
            }
            const parsed = JSON.parse(stored);
            return Array.isArray(parsed) ? parsed : null;
        } catch (error) {
            console.error("Failed to parse medical history snapshot", error);
            return null;
        }
    };

    const setActiveSnapshotInStorage = (key, ids) => {
        try {
            sessionStorage.setItem(key, JSON.stringify(ids ?? []));
        } catch (error) {
            console.error("Failed to persist medical history snapshot", error);
        }
    };

    const conditionSnapshotKey = `medicalHistoryActiveConditions_${serviceId}`;
    const studySnapshotKey = `medicalHistoryActiveStudies_${serviceId}`;

    // Auto-add new medical conditions when returning to clinical services
    useEffect(() => {
        if (!serviceId || !wasClinicalServiceConfirmed) return;

        const hasActiveRecordsData = hasActiveRecords();

        const activeConditionIds = medicalCondRecords?.filter(r => r.status === "active").map(r => r.id) || [];
        const activeStudyIds = medicalStudyRecords?.filter(r => r.status === "active").map(r => r.id) || [];

        if (!hasActiveRecordsData) {
            setActiveSnapshotInStorage(conditionSnapshotKey, activeConditionIds);
            setActiveSnapshotInStorage(studySnapshotKey, activeStudyIds);
            return;
        }

        const storedConditionSnapshot = getActiveSnapshotFromStorage(conditionSnapshotKey);
        const newConditionIds = storedConditionSnapshot
            ? activeConditionIds.filter((id) => !storedConditionSnapshot.includes(id))
            : [];

        if (newConditionIds.length > 0) {
            setConfirmedMedicalConditions((prev) => {
                const merged = [...prev];
                newConditionIds.forEach((id) => {
                    if (!merged.includes(id)) {
                        merged.push(id);
                    }
                });
                return merged;
            });
        }

        setActiveSnapshotInStorage(conditionSnapshotKey, activeConditionIds);
        setActiveSnapshotInStorage(studySnapshotKey, activeStudyIds);
    }, [serviceId, wasClinicalServiceConfirmed, medicalCondRecords, medicalStudyRecords]);

    // Listen for toast-triggered events from patient chart
    useEffect(() => {
        const handleMedicalConditionAdded = (event) => {
            if (serviceId && wasClinicalServiceConfirmed) {
                const newCondition = event.detail.condition;
                if (newCondition?.id && newCondition.status === "active") {
                    setConfirmedMedicalConditions((prev) => {
                        if (!prev.includes(newCondition.id)) {
                            const updated = [...prev, newCondition.id];
                            const activeIds = medicalCondRecords?.filter(r => r.status === "active").map(r => r.id) || [];
                            setActiveSnapshotInStorage(conditionSnapshotKey, Array.from(new Set([...activeIds, newCondition.id])));
                            return updated;
                        }
                        return prev;
                    });
                }
            }
        };

        window.addEventListener('medicalConditionAdded', handleMedicalConditionAdded);
        return () => window.removeEventListener('medicalConditionAdded', handleMedicalConditionAdded);
    }, [serviceId, wasClinicalServiceConfirmed, medicalCondRecords]);

    //#region helper functions

    const isAllConfirmed = () => {
        let isAllMCConfirmed = false;
        let isAllMSConfirmed = false;

        if (medicalCondRecords.filter((item) => item.status === 'active').length > 0) {
            isAllMCConfirmed = medicalCondRecords.filter((item) => item.status === 'active' && !confirmedMedicalConditions.includes(item.id)).length === 0;
        } else {
            isAllMCConfirmed = true;
        }

        if (medicalStudyRecords.filter((item) => item.status === 'active').length > 0) {
            isAllMSConfirmed = medicalStudyRecords.filter((item) => item.status === 'active' && !confirmedMedicalStudies.includes(item.id)).length === 0;
        } else {
            isAllMSConfirmed = true;
        }

        return isAllMCConfirmed && isAllMSConfirmed;
    }

    const setTypeFilter = (filter) => {
        if (create) {
            setstatusFilter(filter);
        }
    }

    const isAnyHistory = () => {
        return medicalCondRecords.length > 0 || medicalStudyRecords.length > 0 || medicalSurgeriesRecords.length > 0;
    }

    const hasActiveRecords = () => {
        return medicalCondRecords.some((item) => item.status === 'active') ||
               medicalStudyRecords.some((item) => item.status === 'active') ||
               medicalSurgeriesRecords.some((item) => item.status === 'active');
    }

    const renderCommentsTooltip = (comment) => (
        <div className="bg-white max-w-[460px]">
            <div className="flex flex-col gap-3 p-2">
                <div className="text-teal-300 text-sm-body prose-a:underline">
                    <div className="flex flex-col gap-4">
                        <p style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                            {formatWordsIntoLines(comment)}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );

    const manageClick = () => {
        document.body.classList.toggle("overflow-hidden");
    };
    //#endregion

    //#region Data update functions
    const deleteHistory = (type, recordId) => {
        var records = [];
        if (type === MedicalHistoryTypes.Condition) records = medicalCondRecords;
        else if (type === MedicalHistoryTypes.Study) records = medicalStudyRecords;
        else if (type === MedicalHistoryTypes.Surgery) records = medicalSurgeriesRecords;

        if (type === MedicalHistoryTypes.Surgery) {
            // TODO: ask dhruv regarding is surgery is special case.
            dispatch(deleteMedicalSurgery(recordId));
        } else {
            // if history is confirmed then have to remove it from that list
            if (selectedHistoryType === MedicalHistoryTypes.Condition && confirmedMedicalConditions.includes(idToDelete)) {
                setConfirmedMedicalConditions(confirmedMedicalConditions.filter(val => val !== idToDelete));
            }

            if (selectedHistoryType === MedicalHistoryTypes.Study && confirmedMedicalStudies.includes(idToDelete)) {
                setConfirmedMedicalStudies(confirmedMedicalStudies.filter(val => val !== idToDelete));
            }

            const deleteObject = records.find((item) => item.id === recordId);
            deleteObject.status = "inactive";

            let obj = {
                noKnownMedicalHistory: records.every(val => val.status === "inactive"),
                medicalConditions: type === MedicalHistoryTypes.Condition ? records : medicalHistory.medicalConditions,
                medicalStudies: type === MedicalHistoryTypes.Study ? records : medicalHistory.medicalStudies,
                surgeries: medicalHistory.surgeries,
            };
            dispatch(updateMedicalHistory(patientId, obj, 'deleted'));
        }
    };

    const restoreHistory = (type, recordId) => {
        var records = [];
        if (type === MedicalHistoryTypes.Condition) records = medicalCondRecords;
        else if (type === MedicalHistoryTypes.Study) records = medicalStudyRecords;
        else if (type === MedicalHistoryTypes.Surgery) records = medicalSurgeriesRecords;

        const restoreObject = records.find((item) => item.id === recordId);
        restoreObject.status = "active";
        restoreObject.confirmedDate = null;

        let obj = {
            noKnownMedicalHistory: records.every(val => val.status === "inactive"),
            medicalConditions: type === MedicalHistoryTypes.Condition ? records : medicalHistory.medicalConditions,
            medicalStudies: type === MedicalHistoryTypes.Study ? records : medicalHistory.medicalStudies,
            surgeries: medicalHistory.surgeries,
        };
        dispatch(updateMedicalHistory(patientId, obj, 'restored'));
    };

    const onNext = async () => {
        if (currentStep <= clinicalServiceSteps.length) {
            if (isNavigating) return;
            setIsNavigating(true);
            const { nextStep, nextSubStep } = getValidStep(clinicalServiceSteps, currentStep, currentSubStep, 1);
            
            const updatedHistory = {
                noKnownMedicalHistory: selectedTab === MedicalHistoryTab.NoMedicalHisotry && isAllHistoryConfirmed,
                medicalConditions: selectedTab === MedicalHistoryTab.NoMedicalHisotry && isAllHistoryConfirmed ? null : medicalCondRecords,
                medicalStudies: selectedTab === MedicalHistoryTab.NoMedicalHisotry && isAllHistoryConfirmed ? null : medicalStudyRecords,
                surgeries: selectedTab === MedicalHistoryTab.NoMedicalHisotry && isAllHistoryConfirmed ? null : medicalSurgeriesRecords,
            };
            
            const updatedSteps = {
                clinicalAssessment: {
                    medicalHistory: true,
                    confirmedMedicalConditions: confirmedMedicalConditions,
                    confirmedMedicalStudies: confirmedMedicalStudies,
                    medicalHistoryConfirmed: isAllHistoryConfirmed
                },
                currentStep: nextStep,
                currentSubStep: nextSubStep,
                serviceSteps: clinicalServiceSteps
            };

            // Save confirmation state to sessionStorage for auto-restore
            if (isAllHistoryConfirmed) {
                sessionStorage.setItem(serviceConfirmKey, 'true');
            } else {
                sessionStorage.removeItem(serviceConfirmKey);
            }

            try {
                await dispatch(updateMedicalHistory(patientId, updatedHistory, false));
                await dispatch(updateMedicationReview(serviceId, updatedSteps));
            } finally {
                setIsNavigating(false);
            }
        }
    };

    const onPrevious = async () => {
        if (isNavigating) return;
        setIsNavigating(true);
        const { nextStep, nextSubStep } = getValidStep(clinicalServiceSteps, currentStep, currentSubStep, -1);
        const updatedSteps = { currentStep: nextStep, currentSubStep: nextSubStep };
        try {
            await dispatch(updateMedicationReview(serviceId, updatedSteps));
        } finally {
            setIsNavigating(false);
        }
    };

    useEffect(() => {
        if (medicalHistory) {
        setMedicalCondRecords(medicalHistory.medicalConditions ?? []);
        setMedicalStudyRecords(medicalHistory.medicalStudies ?? []);
        setMedicalSurgeriesRecords(medicalHistory.surgeries ?? []);
            setMedicalStudyRecords(medicalHistory.medicalStudies ?? []);
            setMedicalSurgeriesRecords(medicalHistory.surgeries ?? []);
            
            // Restore confirmed conditions/studies from Redux or backend
            const storedConditions = clinicalServiceObj?.initialAssessment?.initialAssessmentForm?.clinicalAssessment?.confirmedMedicalConditions ?? [];
            const storedStudies = clinicalServiceObj?.initialAssessment?.initialAssessmentForm?.clinicalAssessment?.confirmedMedicalStudies ?? [];
            
            if (storedConditions.length > 0) {
                setConfirmedMedicalConditions(storedConditions);
            } else if (confirmedMedicalConditions.length === 0) {
                const backendConditions = medicalHistory.medicalConditions
                    ?.filter(c => c.status === "active" && c.confirmedDate)
                    ?.map(c => c.id) ?? [];
                if (backendConditions.length > 0) {
                    setConfirmedMedicalConditions(backendConditions);
                }
            }

            if (storedStudies.length > 0) {
                setConfirmedMedicalStudies(storedStudies);
            } else if (confirmedMedicalStudies.length === 0) {
                const backendStudies = medicalHistory.medicalStudies
                    ?.filter(s => s.status === "active" && s.confirmedDate)
                    ?.map(s => s.id) ?? [];
                if (backendStudies.length > 0) {
                    setConfirmedMedicalStudies(backendStudies);
                }
            }

            const serviceConfirmed = clinicalServiceObj.initialAssessment?.initialAssessmentForm?.clinicalAssessment?.medicalHistoryConfirmed;
            const isNewService = !clinicalServiceObj.initialAssessment?.initialAssessmentForm?.clinicalAssessment?.medicalHistory;
            
            // Prioritize Redux, then initialize
            if (serviceConfirmed !== undefined) {
                setIsAllHistoryConfirmed(serviceConfirmed === true);
            } else if (isNewService) {
                setIsAllHistoryConfirmed(false);
            } else if (medicalHistory.noKnownMedicalHistory === true) {
                dispatch(updateMedicationReview(serviceId, {
                    clinicalAssessment: {
                        ...clinicalServiceObj.initialAssessment?.initialAssessmentForm?.clinicalAssessment,
                        medicalHistoryConfirmed: true
                    }
                }));
                setIsAllHistoryConfirmed(true);
            } else {
                setIsAllHistoryConfirmed(false);
            }

            if ((medicalHistory.medicalConditions?.filter(val => val.status === "active")?.length > 0
            || medicalHistory.medicalStudies?.filter(val => val.status === "active")?.length > 0
            || medicalHistory.surgeries?.filter(val => val.status === "active")?.length > 0) && !medicalHistory.noKnownMedicalHistory) {
                setSelectedTab(MedicalHistoryTab.MedicalHistoryInformation);
            } else {
                setSelectedTab(MedicalHistoryTab.NoMedicalHisotry);
            }
        }
    }, [medicalHistory, serviceId])
    
    useEffect(() => {
        if (selectedTab === MedicalHistoryTab.MedicalHistoryInformation) {

            setIsAllHistoryConfirmed(isAllConfirmed());
        }
    }, [medicalCondRecords, medicalStudyRecords, confirmedMedicalConditions, confirmedMedicalStudies]);

    // Switch to Medical History Information tab if active records exist while No Medical History is selected
    useEffect(() => {
        if (selectedTab === MedicalHistoryTab.NoMedicalHisotry && hasActiveRecords()) {
            setSelectedTab(MedicalHistoryTab.MedicalHistoryInformation);
            setIsAllHistoryConfirmed(isAllConfirmed());
        }
    }, [medicalCondRecords, medicalStudyRecords, medicalSurgeriesRecords]);
    //#endregion

    // Pre-compute filtered surgeries and scrollbar state for the Surgeries table
    const filteredSurgeriesForTable = medicalSurgeriesRecords.filter((val) =>
        statusFilter === "all" ? true : val.status === statusFilter
    );
    const shouldShowSurgeriesScrollbar = filteredSurgeriesForTable.length > 6;

    return (
        <div className="relative flex flex-col flex-1 w-full gap-4 bg-white">
            <div className="flex flex-col flex-1">
                <div className="flex flex-1 gap-4">
                    <ChartNav />
                    <Medications />
                    <div className="flex flex-1 h-full">
                        <div className="w-full gap-5 h-full">
                            <div className="flex items-center justify-between mt-0.5 mb-0.5">
                                <h4 className="text-2xl font-bold text-teal-600">
                                    Medical History
                                </h4>
                                <SubNavigation />
                            </div>
                            <div className="border-b-[7px] border-gray-20 mb-[20px]"></div>

                            {/* option boxes for no medical histoy and medical history information */}
                            <div id="medicalHistoryContainer" className="flex flex-col h-[590px] gap-6 overflow-scroll overflow-y-auto overflow-x-hidden bg-scroll">
                                {/* No medical history */}
                                <div
                                    className={`flex justify-between p-6 border rounded 
                                        ${selectedTab === MedicalHistoryTab.NoMedicalHisotry
                                            ? "border-gray-100 border-2 bg-gray-20"
                                            : "border-gray-100"
                                        }`}
                                >
                                    <div className="flex">
                                        <div className="flex-col justify-center items-center pr-6">
                                            <h3>
                                                <input
                                                    id="in-person"
                                                    name="visit-type"
                                                    onChange={() => {
                                                        if (!hasActiveRecords()) {
                                                            setSelectedTab(MedicalHistoryTab.NoMedicalHisotry);
                                                            const serviceConfirmed = clinicalServiceObj?.initialAssessment?.initialAssessmentForm?.clinicalAssessment?.medicalHistoryConfirmed;
                                                            if (serviceConfirmed !== true) {
                                                                setIsAllHistoryConfirmed(false);
                                                            }
                                                        }
                                                    }}
                                                    value={selectedTab === MedicalHistoryTab.NoMedicalHisotry}
                                                    checked={selectedTab === MedicalHistoryTab.NoMedicalHisotry}
                                                    type="radio"
                                                    disabled={hasActiveRecords()}
                                                    className={`w-[45px] h-[45px] border-gray-100 form-radio text-REELTeal focus:ring-transparent ${
                                                        hasActiveRecords()
                                                            ? "cursor-not-allowed opacity-60"
                                                            : "cursor-pointer"
                                                    }`}
                                                    data-tooltip-id={hasActiveRecords() ? "no-medical-history-disabled-tooltip" : undefined}
                                                />
                                            </h3>
                                        </div>
                                        <div className="flex flex-col">
                                            <h3 className={`text-xl font-semibold ${hasActiveRecords() ? "text-gray-500" : "text-gray-900"}`}>
                                                No Medical History
                                            </h3>
                                            <p className={`text-base ${hasActiveRecords() ? "text-gray-500" : "text-gray-800"}`}>
                                                Medical Conditions, Medical Studies, Past Surgeries/
                                                Procedures.
                                            </p>
                                        </div>
                                    </div>
                                    {selectedTab === MedicalHistoryTab.NoMedicalHisotry && (
                                        <div className="flex flex-col">
                                            <Button
                                                className="text-base rounded-[4px] !w-[200px] !h-full"
                                                type={isAllHistoryConfirmed ? "primary" : "primary-outline"}
                                                text={isAllHistoryConfirmed ? "Confirmed" : "Click to Confirm"}
                                                onClick={() => {
                                                    if (!isAllHistoryConfirmed) {
                                                        dispatch(updateMedicationReview(serviceId, {
                                                            clinicalAssessment: {
                                                                ...clinicalServiceObj.initialAssessment?.initialAssessmentForm?.clinicalAssessment,
                                                                medicalHistoryConfirmed: true
                                                            }
                                                        }));
                                                        setIsAllHistoryConfirmed(true);
                                                    }
                                                }}
                                            />
                                        </div>
                                    )}
                                    {hasActiveRecords() && (
                                        <NewToolTip
                                            id="no-medical-history-disabled-tooltip"
                                            place="bottom-start"
                                            render={() => (
                                                <div className="bg-gray-20 p-2 max-w-md flex flex-col">
                                                    <p className="text-teal-300 text-left text-body-sm font-normal">
                                                        Delete all medical conditions to enable 'No Medical History'.
                                                    </p>
                                                </div>
                                            )}
                                        />
                                    )}
                                </div>
                                {/* Medical history information */}
                                <div
                                    className={`flex flex-col gap-6 p-6 border rounded ${selectedTab === MedicalHistoryTab.MedicalHistoryInformation
                                        ? "border-gray-100 border-2 bg-gray-20"
                                        : "border-gray-100"
                                        }`}
                                >
                                    {/* Start: Header */}
                                    <div className="flex justify-between">
                                        <div className="flex">
                                            <div className="flex justify-center items-center pr-6">
                                                <h3>
                                                    <input
                                                        id="in-person"
                                                        name="visit-type"
                                                        onChange={() => {
                                                            setSelectedTab(MedicalHistoryTab.MedicalHistoryInformation);
                                                            setIsAllHistoryConfirmed(isAllConfirmed());

                                                            if (!isAnyHistory()) {
                                                                setShowCreateSlider(true);
                                                            } else {
                                                                setShowCreateSlider(false);
                                                            }
                                                        }}
                                                        value={selectedTab === MedicalHistoryTab.MedicalHistoryInformation}
                                                        checked={selectedTab === MedicalHistoryTab.MedicalHistoryInformation}
                                                        type="radio"
                                                        className="w-[45px] h-[45px] border-gray-100 cursor-pointer form-radio text-REELTeal focus:ring-transparent"
                                                    />
                                                </h3>
                                            </div>
                                            <div className="flex flex-col">
                                                <h3 className="text-xl font-semibold text-gray-900">
                                                    {isAnyHistory() ? "Medical History Information" : "Add Medical History"}
                                                </h3>
                                                <p className="text-base text-gray-800">
                                                    Medical Conditions, Medical Studies, Past Surgeries/
                                                    Procedures.{" "}
                                                    {isAnyHistory() && (
                                                        <span className="text-sm text-teal-300">
                                                            (Delete all medical conditions to enable ‘No Known Medical Conditions’.)
                                                        </span>
                                                    )}
                                                </p>
                                            </div>
                                        </div>
                                        {selectedTab === MedicalHistoryTab.MedicalHistoryInformation && (
                                            <div className="flex flex-row pr-3.5">
                                                <Menu
                                                    as="div"
                                                    className="relative inline-block text-left pt-[6px]"
                                                >
                                                    <div className="flex items-center">
                                                        <Menu.Button className="flex flex-row font-semibold mr-6 bg-white py-[14px] rounded-[4px] px-[16px] text-base min-w-[220px] h-full border-[0px] focus:outline-[0px] focus:ring-[0px] text-gray-900 p-0 focus:outline-none disabled:cursor-not-allowed text-left text-body-md items-center justify-between">
                                                            <span className="flex">
                                                                {
                                                                    StatusOptions.find(
                                                                        (x) => x.value === statusFilter
                                                                    ).label
                                                                }
                                                            </span>
                                                            <div className="flex flex-col">
                                                                <span>
                                                                    <ChevronDownIcon
                                                                        className={`w-5 transform transition-all `}
                                                                    />
                                                                </span>
                                                            </div>
                                                        </Menu.Button>
                                                    </div>
                                                    <Transition
                                                        as={Fragment}
                                                        enter="transition ease-out duration-100"
                                                        enterFrom="transform translate-y-2 opacity-0 scale-95"
                                                        enterTo="transform translate-y-0 opacity-100 scale-100"
                                                        leave="transition ease-in duration-75"
                                                        leaveFrom="transform translate-y-0 opacity-100 scale-100"
                                                        leaveTo="transform translate-y-2 opacity-0 scale-95"
                                                    >
                                                        <Menu.Items className="min-w-[220px] absolute z-10 left-0 w-40 origin-top-left bg-white divide-y divide-gray-100 rounded-md shadow-lg ring-1 ring-black ring-opacity-5">
                                                            {StatusOptions.map((status) => (
                                                                <Menu.Item
                                                                    className={`block w-full text-left px-3 py-3 border-b border-b-gray-20 
                                                                                ${statusFilter === status.value && "bg-blue-100"} hover:bg-gray-50`}
                                                                    onClick={(e) => {
                                                                        setstatusFilter(status.value);
                                                                    }}
                                                                >
                                                                    <span>{status.label}</span>
                                                                </Menu.Item>
                                                            ))}
                                                            <Menu.Item className={`relative`}>
                                                                <Menu
                                                                    as="div"
                                                                    className="block w-full px-3 py-3 border-b border-b-gray-20 hover:bg-gray-50"
                                                                >
                                                                    <div>
                                                                        <Menu.Button className="w-full flex flex-row items-center justify-between text-left">
                                                                            <span>History Type</span>
                                                                            <ChevronRightIcon className="w-4 h-4" />
                                                                        </Menu.Button>
                                                                    </div>
                                                                    <Transition
                                                                        as={Fragment}
                                                                        enter="transition ease-out duration-100"
                                                                        enterFrom="transform translate-y-2 opacity-0 scale-95"
                                                                        enterTo="transform translate-y-0 opacity-100 scale-100"
                                                                        leave="transition ease-in duration-75"
                                                                        leaveFrom="transform translate-y-0 opacity-100 scale-100"
                                                                        leaveTo="transform translate-y-2 opacity-0 scale-95"
                                                                    >
                                                                        <Menu.Items className="min-w-[200px] absolute left-5 top-0 translate-x-full bg-white divide-y divide-gray-100 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                                                            {TypeOptions.map((type) => (
                                                                                <Menu.Item key={type.value}>
                                                                                    <span
                                                                                        className={`block w-full text-left px-3 py-3 border-b border-b-gray-20 ${shownHistoryType === type.value &&
                                                                                            "bg-blue-100"
                                                                                            } hover:bg-gray-50`}
                                                                                        onClick={(e) => {
                                                                                            setShownHistoryType(type.value);
                                                                                        }}
                                                                                    >
                                                                                        {type.label}
                                                                                    </span>
                                                                                </Menu.Item>
                                                                            ))}
                                                                        </Menu.Items>
                                                                    </Transition>
                                                                </Menu>
                                                            </Menu.Item>
                                                        </Menu.Items>
                                                    </Transition>
                                                </Menu>
                                                <Button
                                                    className="flex rounded-[4px] text-base !w-[220px]"
                                                    type="primary"
                                                    text="Add Medical History"
                                                    onClick={() => {
                                                        setShowCreateSlider(true);
                                                    }}
                                                />
                                            </div>
                                        )}
                                    </div>
                                    {/* End: Header */}
                                    {/* Start: Medical condition table */}
                                    {selectedTab === MedicalHistoryTab.MedicalHistoryInformation && medicalCondRecords?.filter(val => statusFilter === "all" ? true : val.status === statusFilter).length > 0 &&
                                        (shownHistoryType === "all" || shownHistoryType === "medicalCondition") && (
                                            <div id="medicalConditionsTable">
                                                <HistoryTable
                                                    headerName="Medical Conditions"
                                                    isMedicalConditionTable={true}
                                                    records={medicalCondRecords.filter(val => statusFilter === "all" ? true : val.status === statusFilter)}
                                                    confirmedRecords={confirmedMedicalConditions}
                                                    onConfirm={(id) => {
                                                        if (!confirmedMedicalConditions.includes(id)) {
                                                            setConfirmedMedicalConditions([...confirmedMedicalConditions, id]);
                                                        } else {
                                                            setConfirmedMedicalConditions(confirmedMedicalConditions.filter(val => val !== id));
                                                        }
                                                    }}
                                                    onConfirmAll={() => {
                                                        const allConditionIds = medicalCondRecords.filter(r => r.status === "active").map(val => val.id);
                                                        setConfirmedMedicalConditions(allConditionIds);
                                                        
                                                        if(medicalStudyRecords?.length > 0) {
                                                            setTimeout(() => {
                                                                const medicalStudiesElement = document.getElementById('medicalStudiesTable');
                                                                if (medicalStudiesElement) {
                                                                    medicalStudiesElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                                                }
                                                            }, 100);
                                                        }
                                                    }}
                                                    onEdit={(condition) => {
                                                        setSelectedHistory(condition);
                                                        setSelectedHistoryType(MedicalHistoryTypes.Condition);
                                                        setCreate(false);
                                                        setShowCreateSlider(true);
                                                    }}
                                                    onDelete={(condition) => {
                                                        setSelectedHistory(condition);
                                                        setSelectedHistoryType(MedicalHistoryTypes.Condition);
                                                        setOpenDeleteHistory(true);
                                                        setIdToDelete(condition.id);
                                                    }}
                                                    onRestore={(condition) => {
                                                        setSelectedHistory(condition);
                                                        setSelectedHistoryType(MedicalHistoryTypes.Condition);
                                                        setOpenRestoreHistory(true);
                                                        setIdToRestore(condition.id);
                                                    }}
                                                />
                                            </div>
                                        )}
                                    {/* End: Medical condition table */}

                                    {/* Start: Medical study */}
                                    {selectedTab === MedicalHistoryTab.MedicalHistoryInformation && medicalStudyRecords?.filter(val => statusFilter === "all" ? true : val.status === statusFilter).length > 0 &&
                                        (shownHistoryType === "all" || shownHistoryType === "medicalStudy") && (
                                            <div id="medicalStudiesTable">
                                                <HistoryTable
                                                    headerName="Medical Studies"
                                                    isMedicalConditionTable={false}
                                                    records={medicalStudyRecords.filter(val => statusFilter === "all" ? true : val.status === statusFilter)}
                                                    confirmedRecords={confirmedMedicalStudies}
                                                    onConfirm={(id) => {
                                                        if (!confirmedMedicalStudies.includes(id)) {
                                                            setConfirmedMedicalStudies([...confirmedMedicalStudies, id]);
                                                        } else {
                                                            setConfirmedMedicalStudies(confirmedMedicalStudies.filter(val => val !== id));
                                                        }
                                                    }}
                                                    onConfirmAll={() => {
                                                        const allStudyIds = medicalStudyRecords.filter(r => r.status === "active").map(val => val.id);
                                                        setConfirmedMedicalStudies(allStudyIds);
                                                        
                                                        if(medicalSurgeriesRecords?.length > 0) {
                                                            setTimeout(() => {
                                                                const surgeriesElement = document.getElementById('surgeriesTable');
                                                                if (surgeriesElement) {
                                                                    surgeriesElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                                                }
                                                            }, 100);
                                                        }
                                                    }}
                                                    onEdit={(study) => {
                                                        setSelectedHistory(study);
                                                        setSelectedHistoryType(MedicalHistoryTypes.Study);
                                                        setCreate(false);
                                                        setShowCreateSlider(true);
                                                    }}
                                                    onDelete={(study) => {
                                                        setSelectedHistory(study);
                                                        setSelectedHistoryType(MedicalHistoryTypes.Study);
                                                        setOpenDeleteHistory(true);
                                                        setIdToDelete(study.id);
                                                    }}
                                                    onRestore={(study) => {
                                                        setSelectedHistory(study);
                                                        setSelectedHistoryType(MedicalHistoryTypes.Study);
                                                        setOpenRestoreHistory(true);
                                                        setIdToRestore(study.id);
                                                    }}
                                                />
                                            </div>
                                        )}
                                    {/* End: Medical study */}
                                    {/* Start: Surgeries/ Procedures */}
                                    {selectedTab === MedicalHistoryTab.MedicalHistoryInformation && medicalSurgeriesRecords.length > 0 && (shownHistoryType === "all" || shownHistoryType === "medicalSurgery") && (
                                        <div id="surgeriesTable">
                                            {filteredSurgeriesForTable.length > 0 && (
                                                <div className="rounded-[6px] bg-white border border-gray-100 p-3">
                                                    <h4 className="text-2xl font-bold text-teal-600 pb-2 pl-2">
                                                        Surgeries/ Procedures
                                                    </h4>
                                                    <div className="rounded-[6px] border border-gray-100">
                                                        <div
                                                            className={`w-full ${
                                                                shouldShowSurgeriesScrollbar
                                                                    ? "overflow-y-auto scrollbar-hide max-h-[290px]"
                                                                    : ""
                                                            }`}
                                                        >
                                                        <table className="min-w-full divide-y bg-gray-50">
                                                            <thead className="bg-gray-20 items-start gap-4">
                                                                <tr>
                                                                    <th
                                                                        scope="col"
                                                                        className="pl-6 w-[27%] text-base font-semibold text-left text-gray-900 py-4"
                                                                    >
                                                                        Surgeries/ Procedures
                                                                    </th>
                                                                    <th
                                                                        scope="col"
                                                                        className="w-[26%] text-base font-semibold text-left text-gray-900"
                                                                    >
                                                                        Status
                                                                    </th>
                                                                    <th
                                                                        scope="col"
                                                                        className="w-[13%] text-base font-semibold text-left text-gray-900"
                                                                    >
                                                                        Source
                                                                    </th>
                                                                    <th
                                                                        scope="col"
                                                                        className="w-[14%] text-base font-semibold text-left text-gray-900"
                                                                    >
                                                                        Diagnosis Date
                                                                    </th>
                                                                    <th
                                                                        scope="col"
                                                                        className="w-[14%] text-base font-semibold text-left text-gray-900"
                                                                    >
                                                                        Comments
                                                                    </th>
                                                                    <th
                                                                        scope="col"
                                                                        className="w-[22%] text-base font-semibold text-left text-gray-900"
                                                                    > Actions</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody className="min-w-full divide-y bg-white gap-4">
                                                                {filteredSurgeriesForTable.map((sur, i) => (
                                                                    <tr
                                                                        className={`bg-white transition-colors duration-300 m-4 cursor-pointer group `}
                                                                        key={sur.id}
                                                                    >
                                                                        <td
                                                                            className={`pl-6 py-2 w-[27%] text-base font-medium text-teal-300 whitespace-nowrap 
                                                                                    ${sur.surgeryGenCode === null
                                                                                    ? "text-pink"
                                                                                    : "text-gray-800"
                                                                                }`}
                                                                            data-tooltip-id={
                                                                                sur.surgeryGenCode === null
                                                                                    ? "ma-uncoded-medSurgery"
                                                                                    : ""
                                                                            }
                                                                        >
                                                                            <span className="capitalize">
                                                                                {sur.name}
                                                                            </span>
                                                                            {sur.surgeryGenCode === null && (
                                                                                <NewToolTip
                                                                                    id="ma-uncoded-medSurgery"
                                                                                    render={() => <UncodedMedical />}
                                                                                />
                                                                            )}
                                                                        </td>
                                                                        <td className="w-[26%] text-base font-medium text-gray-800 whitespace-nowrap">
                                                                            {sur.status}
                                                                        </td>
                                                                        <td className="w-[13%] text-base font-medium text-gray-800 whitespace-nowrap">
                                                                            {sur.source}
                                                                        </td>
                                                                        <td className="w-[14%] text-base text-gray-800 whitespace-nowrap">
                                                                            {sur.date != null
                                                                                ? formatDateToDD_MMM_YYYY(sur.date)
                                                                                : ""}
                                                                        </td>
                                                                        <td className="w-[14%] flex flex-row items-center justify-end pt-2 text-base font-medium text-gray-800 whitespace-nowrap px-16">
                                                                            {sur.comments && sur.comments.length > 0 && (
                                                                                <div className="flex w-full justify-end items-center">
                                                                                    <div
                                                                                        ref={openTooltipId === `medSurgComments-${sur.id}` ? tooltipWrapperRef : null}
                                                                                        className="relative inline-flex cursor-pointer"
                                                                                        data-tooltip-id={`medSurgCommentsHover-${sur.id}`}
                                                                                        onClick={(e) => {
                                                                                            e.stopPropagation();
                                                                                            const tooltipId = `medSurgComments-${sur.id}`;
                                                                                            setOpenTooltipId(openTooltipId === tooltipId ? null : tooltipId);
                                                                                        }}
                                                                                    >
                                                                                        <ReactSVG
                                                                                            src="/icons/paper-clip-new.svg"
                                                                                            className="w-6"
                                                                                        />
                                                                                        {openTooltipId !== `medSurgComments-${sur.id}` && (
                                                                                            <NewToolTip
                                                                                                id={`medSurgCommentsHover-${sur.id}`}
                                                                                                render={() => (
                                                                                                    <div className="bg-white p-2 text-teal-300 text-body-sm">
                                                                                                        Click to view comments
                                                                                                    </div>
                                                                                                )}
                                                                                            />
                                                                                        )}
                                                                                        <NewToolTip
                                                                                            id={`medSurgComments-${sur.id}`}
                                                                                            isOpen={openTooltipId === `medSurgComments-${sur.id}`}
                                                                                            render={() => renderCommentsTooltip(sur.comments)}
                                                                                            clickable={true}
                                                                                            handleHide={() => setOpenTooltipId(null)}
                                                                                            anchorSelect={`[data-tooltip-id="medSurgCommentsHover-${sur.id}"]`}
                                                                                        />
                                                                                    </div>
                                                                                </div>
                                                                            )}
                                                                        </td>
                                                                        <td className="w-[22%] text-base font-medium text-gray-800 whitespace-nowrap">
                                                                            {sur.status !== "inactive" && (
                                                                                <div className="flex items-center pl-4">
                                                                                    <div className="flex flex-1 justify-center">
                                                                                        <Menu
                                                                                            as="div"
                                                                                            className="relative inline-block text-left"
                                                                                        >
                                                                                            {({ open }) => {
                                                                                                // Track which surgery row's menu is open
                                                                                                if (open && openSurgeryMenuIndex !== i) {
                                                                                                    setOpenSurgeryMenuIndex(i);
                                                                                                } else if (!open && openSurgeryMenuIndex === i) {
                                                                                                    setOpenSurgeryMenuIndex(null);
                                                                                                }

                                                                                                // Hide icon if it's directly below an open menu (to avoid overlap)
                                                                                                const shouldHideIcon = openSurgeryMenuIndex === i - 1;

                                                                                                return (
                                                                                                    <>
                                                                                                        <div>
                                                                                                            <Menu.Button>
                                                                                                                <ReactSVG
                                                                                                                    src={`/icons/triple-dot.svg`}
                                                                                                                    className={`flex text-black !items-center w-6 ${shouldHideIcon ? 'opacity-0 pointer-events-none' : ''}`}
                                                                                                                />
                                                                                                            </Menu.Button>
                                                                                                        </div>
                                                                                                        <Transition
                                                                                                            as={Fragment}
                                                                                                            enter="transition ease-out duration-100"
                                                                                                            enterFrom="transform translate-y-2 opacity-0 scale-95"
                                                                                                            enterTo="transform translate-y-0 opacity-100 scale-100"
                                                                                                            leave="transition ease-in duration-75"
                                                                                                            leaveFrom="transform translate-y-0 opacity-100 scale-100"
                                                                                                            leaveTo="transform translate-y-2 opacity-0 scale-95"
                                                                                                        >
                                                                                                            <Menu.Items
                                                                                                                className={
                                                                                                                    i === filteredSurgeriesForTable.length - 1 || filteredSurgeriesForTable.length === 1
                                                                                                                    ? "absolute right-full mr-[6px] min-w-[70px] w-auto origin-top-right bg-white divide-y divide-gray-20 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 top-0"
                                                                                                                    : "absolute right-4 top-6 w-18 origin-top-right bg-white divide-y divide-gray-20 rounded-md shadow-lg ring-1 ring-black ring-opacity-5"
                                                                                                                }
                                                                                                            >
                                                                                                                <Menu.Item>
                                                                                                                    <button
                                                                                                                        className="block w-full text-left h-[30px] px-2.5 py-2.5 flex items-center border-b border-b-gray-20 hover:bg-gray-100"
                                                                                                                        onClick={(e) => {
                                                                                                                            setSelectedHistory(sur);
                                                                                                                            setSelectedHistoryType(MedicalHistoryTypes.Surgery);
                                                                                                                            setCreate(false);
                                                                                                                            setShowCreateSlider(true);
                                                                                                                        }}
                                                                                                                    >
                                                                                                                        Edit
                                                                                                                    </button>
                                                                                                                </Menu.Item>
                                                                                                                <Menu.Item>
                                                                                                                    <button
                                                                                                                        className="block w-full text-left h-[30px] px-2.5 py-2.5 flex items-center border-b border-b-gray-20 hover:bg-gray-100"
                                                                                                                        onClick={() => {
                                                                                                                            setSelectedHistory(sur);
                                                                                                                            setSelectedHistoryType(MedicalHistoryTypes.Surgery);
                                                                                                                            setOpenDeleteHistory(true);
                                                                                                                            setIdToDelete(sur.id);
                                                                                                                        }}
                                                                                                                    >
                                                                                                                        Delete
                                                                                                                    </button>
                                                                                                                </Menu.Item>
                                                                                                            </Menu.Items>
                                                                                                        </Transition>
                                                                                                    </>
                                                                                                );
                                                                                            }}
                                                                                        </Menu>
                                                                                    </div>
                                                                                </div>
                                                                            )}
                                                                            {sur.status === "inactive" && (
                                                                                <div className="flex justify-end pl-16">
                                                                                    <ReactSVG
                                                                                        src="/icons/undo.svg"
                                                                                        className="w-6"
                                                                                        onClick={() => {
                                                                                            setSelectedHistory(sur);
                                                                                            setSelectedHistoryType(MedicalHistoryTypes.Surgery);
                                                                                            setOpenRestoreHistory(true);
                                                                                            setIdToRestore(sur.id);
                                                                                        }}
                                                                                    />
                                                                                </div>
                                                                            )}
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* End: Surgeries/ Procedures */}

                                    {/* Start: Edit/Delete/Restore pop-ups */}
                                    <ConfirmationModalNew
                                        type="primary"
                                        title={`Delete ${getHistoryTypeName(selectedHistoryType)}`}
                                        content={() => `Are you sure you want to delete ${capitalizeFirstLetter(selectedHistory?.name ?? "")}?`}
                                        confirmAction={() => {
                                            deleteHistory(selectedHistoryType, idToDelete);
                                            setSelectedHistory(null);
                                            setOpenDeleteHistory(false);
                                            setIdToDelete(null);
                                        }}
                                        optionLabel="Reason"
                                        open={openDeleteHistory}
                                        handleClose={() => {
                                            setSelectedHistory(null);
                                            setOpenDeleteHistory(false);
                                            setIdToDelete(null);
                                        }}
                                        onCross={() => {
                                            setSelectedHistory(null);
                                            setOpenDeleteHistory(false);
                                            setIdToDelete(null);
                                        }}
                                        options={DeleteOptions}
                                    />
                                    <ConfirmationModalNew
                                        open={openRestoreHistory}
                                        title={`Restore ${getHistoryTypeName(selectedHistoryType)}`}
                                        type="primary"
                                        confirmText="Yes"
                                        content={() => (
                                            <p className="p-7">
                                                Are you sure you wish to restore {capitalizeFirstLetter(
                                                    selectedHistory?.name || selectedHistory?.conditionName || selectedHistory?.studyName || getHistoryTypeName(selectedHistoryType)
                                                )}?
                                            </p>
                                        )}
                                        onCross={() => {
                                            setSelectedHistory(null);
                                            setOpenRestoreHistory(false);
                                            setIdToRestore(null);
                                        }}
                                        handleClose={() => {
                                            setSelectedHistory(null);
                                            setOpenRestoreHistory(false);
                                            setIdToRestore(null);
                                        }}
                                        confirmAction={() => {
                                            restoreHistory(selectedHistoryType, idToRestore);
                                            setSelectedHistory(null);
                                            setOpenRestoreHistory(false);
                                            setIdToRestore(null);
                                        }}
                                    />
                                    {/* End: Edit/Delete/Restore pop-ups */}

                                    <MedicalHistoryCreate
                                        open={showCreateSlider}
                                        onClose={() => {
                                            setShowCreateSlider(false);
                                            manageClick();
                                            setSelectedHistory(null);
                                            setCreate(true);
                                        }}
                                        selectedHistoryType={selectedHistoryType}
                                        onSave={() => {
                                            setTypeFilter("all");
                                            // Refetch medical conditions to update Overview and MedicalConditionListPopup
                                            if (selectedHistoryType === MedicalHistoryTypes.Condition) {
                                                setTimeout(() => {
                                                    dispatch(fetchMedicalConditions(patientId));
                                                }, 500);
                                            }
                                        }}
                                        selectedHistory={selectedHistory}
                                        patientId={patientId}
                                        create={create}
                                        patientMedicalConditions={medicalCondRecords}
                                        patientMedicalStudies={medicalStudyRecords}
                                        patientMedicalSurgeries={medicalSurgeriesRecords}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="flex items-center justify-between space-x-4 rounded">
                <div className="w-full ">
                    <Steps position="bottom" stepsList={clinicalServiceSteps} />
                </div>
                <div className="flex items-center gap-3 ">
                    <Button
                        type="primary-outline"
                        text="Back"
                        className="w-[200px] max-h-[52px] !rounded-[30px]"
                        onClick={() => {
                            onPrevious();
                        }}
                    />
                    <Button
                        type="primary"
                        text="Next"
                        className={`w-[200px] max-h-[52px] !rounded-[30px]  ${!isAllHistoryConfirmed && "opacity-40 pointer-events-none"}`}
                        onClick={() => {
                            onNext();
                        }}
                    />
                </div>
            </div>
        </div>
    );
}

export default MedicalHistory;